import { useEffect, useMemo } from 'react';
import { Loader2, Tag, Layers, RotateCcw, Plus, ArrowLeftRight } from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import type { FinanceTransaction } from '@/types/finance';
import type {
  CategoryMapping,
  CostCenterMapping,
  ImportRow,
  InvoiceCandidate,
  LineDecision,
  ReconcileCandidate,
} from '../types';
import { findInvoiceCandidates } from '../invoiceMatcher';
import { looksLikeTransfer } from '../transferDetector';

interface Props {
  rows: ImportRow[];
  categoryMappings: CategoryMapping[];
  costCenterMappings: CostCenterMapping[];
  isImporting: boolean;
  isOfx: boolean;
  accountId: string;
  lineDecisions: LineDecision[];
  onLineDecisionsChange: (decisions: LineDecision[]) => void;
  onBack: () => void;
  onConfirm: () => void;
}

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

function amountsMatch(a: number, b: number): boolean {
  // Match por centavo — extrato OFX e nosso DB ambos guardam 2 casas decimais.
  return Math.round(a * 100) === Math.round(b * 100);
}

export function PreviewStep({
  rows,
  categoryMappings,
  costCenterMappings,
  isImporting,
  isOfx,
  accountId,
  lineDecisions,
  onLineDecisionsChange,
  onBack,
  onConfirm,
}: Props) {
  const { categories, costCenters, transactions, accounts } = useFinance();

  // ---- OFX: matches 1-pra-1 por linha ----
  const candidatesByLine = useMemo<ReconcileCandidate[][]>(() => {
    if (!isOfx || !accountId) return rows.map(() => []);
    return rows.map((row) => {
      if (!row.type) return [];
      return transactions
        .filter(
          (t) =>
            t.accountId === accountId &&
            t.status === 'pending' &&
            t.type === row.type &&
            isSameDay(t.dueDate, row.dueDate) &&
            amountsMatch(Number(t.amount), row.amount),
        )
        .map((t) => ({
          transactionId: t.id,
          description: t.description,
          dueDate: t.dueDate,
          amount: Number(t.amount),
        }));
    });
  }, [rows, transactions, accountId, isOfx]);

  // ---- OFX: invoice matches (1-pra-N: pagamento de fatura cartão) ----
  const invoiceCandidatesByLine = useMemo<InvoiceCandidate[][]>(() => {
    if (!isOfx || !accountId) return rows.map(() => []);
    const cards = accounts.filter((a) => a.type === 'credit_card' && a.isActive);
    if (cards.length === 0) return rows.map(() => []);
    return rows.map((row) => {
      // Apenas linhas de despesa na conta de origem podem ser pagamento de cartão.
      if (row.type !== 'expense') return [];
      return findInvoiceCandidates(
        { amount: row.amount, date: row.dueDate, paymentAccountId: accountId },
        { cards, transactions },
      );
    });
  }, [rows, transactions, accounts, accountId, isOfx]);

  // Mapeamento de external_id → transação existente na mesma conta (para
  // detecção de duplicata em re-import OFX). Usa só transações vivas.
  const existingByExternalId = useMemo(() => {
    const map = new Map<string, FinanceTransaction>();
    if (!accountId) return map;
    for (const t of transactions) {
      if (t.externalId && t.accountId === accountId) {
        map.set(t.externalId, t);
      }
    }
    return map;
  }, [transactions, accountId]);

  // Inicializa decisões: invoice match com 1 cartão → reconcile_invoice;
  // 1 match 1-pra-1 → reconcile; senão → create.

  useEffect(() => {
    if (!isOfx) return;
    if (lineDecisions.length === rows.length) return;
    const next: LineDecision[] = rows.map((_, i) => {
      const invoiceCands = invoiceCandidatesByLine[i] ?? [];
      if (invoiceCands.length === 1) {
        const c = invoiceCands[0]!;
        return {
          kind: 'reconcile_invoice',
          cardAccountId: c.cardAccountId,
          periodStart: c.periodStart,
          periodEnd: c.periodEnd,
          transactionIds: c.transactionIds,
        };
      }
      const cands = candidatesByLine[i] ?? [];
      if (cands.length === 1) {
        return { kind: 'reconcile', transactionId: cands[0]!.transactionId };
      }
      return { kind: 'create' };
    });
    onLineDecisionsChange(next);
  }, [
    isOfx,
    rows,
    candidatesByLine,
    invoiceCandidatesByLine,
    lineDecisions.length,
    onLineDecisionsChange,
  ]);

  const setDecision = (idx: number, decision: LineDecision) => {
    const next = [...lineDecisions];
    next[idx] = decision;
    onLineDecisionsChange(next);
  };

  // ---- Conta Azul (CSV/XLSX): mappings ----
  const catsToCreate = categoryMappings.filter((m) => m.decision.kind === 'create').length;
  const ccsToCreate = costCenterMappings.filter((m) => m.decision.kind === 'create').length;

  const resolveCategory = (name?: string): string => {
    if (!name) return '—';
    const m = categoryMappings.find((x) => x.name === name);
    if (!m || m.decision.kind === 'skip') return '—';
    if (m.decision.kind === 'create') return `+ ${name} (novo)`;
    const target = categories.find(
      (c) => c.id === (m.decision as { kind: 'map'; targetId: string }).targetId,
    );
    return target?.name ?? '—';
  };
  const resolveCC = (name?: string): string => {
    if (!name) return '—';
    const m = costCenterMappings.find((x) => x.name === name);
    if (!m || m.decision.kind === 'skip') return '—';
    if (m.decision.kind === 'create') return `+ ${name} (novo)`;
    const target = costCenters.find(
      (cc) => cc.id === (m.decision as { kind: 'map'; targetId: string }).targetId,
    );
    return target?.name ?? '—';
  };

  // ---- Sumário OFX ----
  const reconcileCount = lineDecisions.filter((d) => d.kind === 'reconcile').length;
  const reconcileInvoiceCount = lineDecisions.filter((d) => d.kind === 'reconcile_invoice').length;
  const createCount = lineDecisions.filter((d) => d.kind === 'create').length;
  const transferCount = lineDecisions.filter((d) => d.kind === 'create_transfer').length;
  const duplicateCount = rows.filter(
    (r) => r.externalId && existingByExternalId.has(r.externalId),
  ).length;

  // Contas elegíveis como destino de transferência: ativas, diferentes da selecionada.
  const transferTargetAccounts = useMemo(
    () => accounts.filter((a) => a.isActive && a.id !== accountId),
    [accounts, accountId],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 text-sm">
        <span className="rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
          {rows.length} {rows.length === 1 ? 'lançamento' : 'lançamentos'}
        </span>
        {isOfx ? (
          <>
            {reconcileCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-700">
                <RotateCcw size={12} /> {reconcileCount} reconciliar
              </span>
            )}
            {reconcileInvoiceCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
                <RotateCcw size={12} /> {reconcileInvoiceCount} fatura(s) cartão
              </span>
            )}
            {createCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-income/10 px-3 py-1 font-medium text-income">
                <Plus size={12} /> {createCount} criar nova(s)
              </span>
            )}
            {transferCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-3 py-1 font-medium text-sky-700">
                <ArrowLeftRight size={12} /> {transferCount} transferência(s)
              </span>
            )}
            {duplicateCount > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-700 ring-1 ring-amber-200"
                title="Linhas com FITID já presente no sistema. Serão ignoradas na importação."
              >
                {duplicateCount} já importada(s) — serão ignoradas
              </span>
            )}
          </>
        ) : (
          <>
            {catsToCreate > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-income/10 px-3 py-1 font-medium text-income">
                <Tag size={12} /> {catsToCreate} categoria(s) nova(s)
              </span>
            )}
            {ccsToCreate > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-income/10 px-3 py-1 font-medium text-income">
                <Layers size={12} /> {ccsToCreate} centro(s) de custo novo(s)
              </span>
            )}
          </>
        )}
      </div>

      <div className="max-h-[400px] overflow-y-auto rounded-lg border border-border bg-white">
        <Table>
          <TableHeader className="sticky top-0 bg-white">
            <TableRow>
              <TableHead className="w-24">Data</TableHead>
              <TableHead>Descrição</TableHead>
              {isOfx ? (
                <>
                  <TableHead className="w-20 text-center">Tipo</TableHead>
                  <TableHead className="w-64">Decisão</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Centro de Custo</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                </>
              )}
              <TableHead className="w-28 text-right">Valor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const cands = candidatesByLine[i] ?? [];
              const invoiceCands = invoiceCandidatesByLine[i] ?? [];
              const decision = lineDecisions[i] ?? { kind: 'create' };
              const isTransferHint = looksLikeTransfer(r.description);
              const canBeTransfer = transferTargetAccounts.length > 0;
              const hasAnyMatch = cands.length > 0 || invoiceCands.length > 0;
              const showSelect = hasAnyMatch || canBeTransfer;
              const selectValue =
                decision.kind === 'reconcile'
                  ? `reconcile:${decision.transactionId}`
                  : decision.kind === 'reconcile_invoice'
                    ? `invoice:${decision.cardAccountId}:${decision.periodStart}:${decision.periodEnd}`
                    : decision.kind === 'create_transfer'
                      ? `transfer:${decision.destinationAccountId}`
                      : 'create';
              const isDuplicate = !!(r.externalId && existingByExternalId.has(r.externalId));
              return (
                <TableRow key={i} className={isDuplicate ? 'bg-muted/40 opacity-70' : ''}>
                  <TableCell className="text-xs">
                    {format(r.dueDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm" title={r.description}>
                    <div className="flex items-center gap-2">
                      <span className="truncate">{r.description}</span>
                      {isDuplicate && (
                        <span
                          className="shrink-0 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                          title="Esta transação já existe (mesmo FITID). Será ignorada na importação."
                        >
                          Já importada
                        </span>
                      )}
                    </div>
                  </TableCell>
                  {isOfx ? (
                    <>
                      <TableCell className="text-center">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            r.type === 'income'
                              ? 'bg-income/10 text-income'
                              : 'bg-expense/10 text-expense'
                          }`}
                        >
                          {r.type === 'income' ? 'Entrada' : 'Saída'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {!showSelect ? (
                          <span className="text-xs text-muted-foreground">Criar nova</span>
                        ) : (
                          <div className="space-y-1">
                            <Select
                              value={selectValue}
                              onValueChange={(v) => {
                                if (v === 'create') {
                                  setDecision(i, { kind: 'create' });
                                } else if (v.startsWith('reconcile:')) {
                                  const id = v.replace(/^reconcile:/, '');
                                  setDecision(i, { kind: 'reconcile', transactionId: id });
                                } else if (v.startsWith('invoice:')) {
                                  const [, cardId, periodStart, periodEnd] = v.split(':');
                                  const inv = invoiceCands.find(
                                    (c) =>
                                      c.cardAccountId === cardId &&
                                      c.periodStart === periodStart &&
                                      c.periodEnd === periodEnd,
                                  );
                                  if (inv) {
                                    setDecision(i, {
                                      kind: 'reconcile_invoice',
                                      cardAccountId: inv.cardAccountId,
                                      periodStart: inv.periodStart,
                                      periodEnd: inv.periodEnd,
                                      transactionIds: inv.transactionIds,
                                    });
                                  }
                                } else if (v.startsWith('transfer:')) {
                                  const destId = v.replace(/^transfer:/, '');
                                  setDecision(i, {
                                    kind: 'create_transfer',
                                    destinationAccountId: destId,
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {invoiceCands.map((c) => (
                                  <SelectItem
                                    key={`${c.cardAccountId}:${c.periodStart}:${c.periodEnd}`}
                                    value={`invoice:${c.cardAccountId}:${c.periodStart}:${c.periodEnd}`}
                                  >
                                    ↻ Fechar fatura {c.cardAccountName} ({c.transactionIds.length}{' '}
                                    itens)
                                  </SelectItem>
                                ))}
                                {cands.map((c) => (
                                  <SelectItem
                                    key={c.transactionId}
                                    value={`reconcile:${c.transactionId}`}
                                  >
                                    ↺ Reconciliar: {c.description}
                                  </SelectItem>
                                ))}
                                <SelectItem value="create">+ Criar nova</SelectItem>
                                {transferTargetAccounts.map((a) => (
                                  <SelectItem key={a.id} value={`transfer:${a.id}`}>
                                    ⇄ Transferir {r.type === 'income' ? 'de' : 'para'} {a.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {isTransferHint && decision.kind !== 'create_transfer' && (
                              <p className="text-[10px] text-sky-700">
                                Parece transferência — selecione a conta destino.
                              </p>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-xs text-muted-foreground">
                        {resolveCategory(r.categoryName)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {resolveCC(r.costCenterName)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span
                          className={
                            r.status === 'completed' ? 'text-income' : 'text-muted-foreground'
                          }
                        >
                          {r.status === 'completed' ? 'Pago' : 'Pendente'}
                        </span>
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right text-sm font-medium tabular-nums">
                    {fmtMoney(r.amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={isImporting}>
          Voltar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isImporting}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          {isImporting ? (
            <>
              <Loader2 size={14} className="mr-2 animate-spin" />
              Importando…
            </>
          ) : (
            'Confirmar importação'
          )}
        </Button>
      </div>
    </div>
  );
}
